/**
 * CombineDeckModal — lets the user pick 2-N captured cards from the
 * Board and stitch them into a single combined deck.
 *
 * Lists every voice-memo and sit-in-meeting card on the current board
 * that has parsed structured data. User checks the ones they want to
 * include, hits "Generate Combined Deck" → the caller (Board.tsx)
 * opens DeckViewer with `combined={…}` and the user is on the same
 * deck flow they already know: navigate slides, download PDF.
 *
 * No additional AI inference happens — buildCombinedDeck is a pure
 * deterministic transformation of the already-parsed payloads.
 */
import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { CombinedSource, MeetingData, MemoData } from './DeckViewer';

// Matches Dashboard / DeckViewer light palette.
const BG          = '#FFFFFF';
const SURFACE     = '#FAFAF8';
const SURFACE_HI  = '#F5F0E8';
const BORDER      = '#E6DDD4';
const ACCENT      = '#b8956a';
const TEXT        = '#0D3D5C';
const TEXT_DIM    = '#6B7280';
const TEXT_FAINT  = '#9CA3AF';

export interface DeckableCard {
  cardId:    string;
  source:    CombinedSource;
  // For the selector list — distilled so we can show a meaningful
  // checkbox row without re-parsing the source on render.
  display: {
    kindLabel: string;       // "Meeting" | "Voice memo"
    title:     string;       // best human title
    subtitle:  string;       // date + duration / type
    summary:   string;       // one-line summary if available
  };
}

interface Props {
  cards:     DeckableCard[];   // every deckable card on the current board
  onClose:   () => void;
  onConfirm: (selected: CombinedSource[]) => void;
}

export default function CombineDeckModal({ cards, onClose, onConfirm }: Props) {
  // Default-select everything available — the most common intent is
  // "fold all my captures on this board into one brief." User can
  // uncheck individual rows if they want a subset.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(cards.map(c => c.cardId)),
  );

  const sorted = useMemo(() => {
    // Most recent first — typically the freshest captures are the ones
    // the user wants in the front of the brief.
    return [...cards].sort((a, b) => {
      const ad = sourceDate(a.source);
      const bd = sourceDate(b.source);
      return bd.localeCompare(ad);
    });
  }, [cards]);

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => setSelected(new Set(cards.map(c => c.cardId)));
  const handleClearAll  = () => setSelected(new Set());

  const handleGenerate = () => {
    const picks: CombinedSource[] = [];
    for (const card of sorted) {
      if (selected.has(card.cardId)) picks.push(card.source);
    }
    if (picks.length === 0) return;
    onConfirm(picks);
  };

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9400,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(620px, 92vw)',
          maxHeight: 'min(720px, 88vh)',
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: 6,
          color: TEXT,
          display: 'flex', flexDirection: 'column',
          fontFamily: '"EB Garamond", Georgia, serif',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
        }}>
        {/* Header */}
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'baseline', gap: 12, flexShrink: 0,
        }}>
          <div className="cd-h" style={{
            fontFamily: 'Fraunces, Georgia, serif', fontSize: 22, color: TEXT,
            letterSpacing: '0.01em',
          }}>
            Combine into deck
          </div>
          <div style={{
            fontSize: 11, color: TEXT_DIM, letterSpacing: '0.04em',
            fontStyle: 'italic',
          }}>
            {selected.size} of {cards.length} selected
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: TEXT_DIM,
            fontSize: 20, cursor: 'pointer', padding: 0, lineHeight: 1,
          }}>×</button>
        </div>

        {/* Subtitle */}
        <div style={{
          padding: '10px 22px', color: TEXT_DIM, fontSize: 12,
          borderBottom: `1px solid ${BORDER}`, lineHeight: 1.5,
          flexShrink: 0,
        }}>
          Pick the captures you want stitched into one combined brief.
          The deck stays strictly grounded — only what was captured will
          appear.{' '}
          <button onClick={handleSelectAll} style={ghostLink}>Select all</button>
          {' · '}
          <button onClick={handleClearAll}  style={ghostLink}>Clear</button>
        </div>

        {/* List */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '6px 12px 12px',
          minHeight: 200,
        }}>
          {sorted.length === 0 ? (
            <div style={{
              padding: '40px 16px', textAlign: 'center',
              color: TEXT_DIM, fontSize: 13, fontStyle: 'italic',
              lineHeight: 1.6,
            }}>
              No deckable cards on this board.
              <br />
              Record a voice memo or sit-in meeting first, then come back.
            </div>
          ) : (
            sorted.map(card => {
              const checked = selected.has(card.cardId);
              return (
                <button
                  key={card.cardId}
                  onClick={() => toggle(card.cardId)}
                  style={{
                    width: '100%', textAlign: 'left',
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '12px 14px', margin: '6px 0',
                    background: checked ? SURFACE_HI : BG,
                    border: `1px solid ${checked ? ACCENT : BORDER}`,
                    borderRadius: 4, cursor: 'pointer',
                    fontFamily: 'inherit', color: TEXT,
                    transition: 'background .15s, border-color .15s',
                  }}>
                  {/* Checkbox visual */}
                  <span style={{
                    flexShrink: 0,
                    width: 16, height: 16, marginTop: 2,
                    borderRadius: 3,
                    border: `1.5px solid ${checked ? ACCENT : BORDER}`,
                    background: checked ? ACCENT : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#FFFFFF', fontSize: 11, fontWeight: 700,
                  }}>
                    {checked ? '✓' : ''}
                  </span>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 9, color: ACCENT, letterSpacing: '0.18em',
                      textTransform: 'uppercase', fontWeight: 700,
                    }}>
                      {card.display.kindLabel}
                    </div>
                    <div className="cd-row-title" style={{
                      fontFamily: 'Fraunces, Georgia, serif',
                      fontSize: 15, color: TEXT, lineHeight: 1.3,
                      marginTop: 2,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any,
                    }}>
                      {card.display.title}
                    </div>
                    {card.display.subtitle && (
                      <div style={{
                        fontSize: 10, color: TEXT_DIM, marginTop: 3,
                        letterSpacing: '0.04em',
                      }}>
                        {card.display.subtitle}
                      </div>
                    )}
                    {card.display.summary && card.display.summary !== card.display.title && (
                      <div style={{
                        fontSize: 11, color: TEXT_DIM, marginTop: 5,
                        fontStyle: 'italic', lineHeight: 1.45,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any,
                      }}>
                        "{card.display.summary}"
                      </div>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 22px', borderTop: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
          background: SURFACE,
        }}>
          <div style={{
            color: selected.size < 2 ? TEXT_FAINT : TEXT_DIM,
            fontSize: 11, fontStyle: 'italic',
          }}>
            {selected.size < 2
              ? 'Select at least 2 captures to combine.'
              : `Will stitch ${selected.size} captures into one combined deck.`}
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={handleGenerate}
            disabled={selected.size < 2}
            style={{
              ...btnPrimary,
              opacity: selected.size < 2 ? 0.4 : 1,
              cursor: selected.size < 2 ? 'not-allowed' : 'pointer',
            }}>
            Generate combined deck
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}


/* ── helpers ──────────────────────────────────────────────────────── */
function sourceDate(s: CombinedSource): string {
  if (s.kind === 'meeting') return s.meeting.recorded_at || '';
  return s.memo.recorded_at || '';
}

const btnPrimary: React.CSSProperties = {
  background: ACCENT, color: '#0a1322', border: 'none',
  padding: '8px 16px', fontSize: 12, fontWeight: 700,
  letterSpacing: '0.06em', textTransform: 'uppercase',
  cursor: 'pointer', borderRadius: 3, fontFamily: 'inherit',
};
const btnGhost: React.CSSProperties = {
  background: 'transparent', color: TEXT_DIM,
  border: `1px solid ${BORDER}`,
  padding: '8px 14px', fontSize: 11,
  letterSpacing: '0.04em', textTransform: 'uppercase',
  cursor: 'pointer', borderRadius: 3, fontFamily: 'inherit',
};
const ghostLink: React.CSSProperties = {
  background: 'transparent', border: 'none', color: ACCENT,
  padding: 0, cursor: 'pointer', fontFamily: 'inherit',
  fontSize: 12, fontWeight: 600,
  textDecoration: 'underline', textDecorationStyle: 'dotted',
};

// Helper that callers (Board.tsx) use to build the DeckableCard list
// from raw board state. Defined here so the data-shape contract stays
// colocated with the modal that consumes it.
export function deckableCardsFromBoard(cards: Array<{
  id: string; type: string;
  voice?: any; meeting?: any;
}>): DeckableCard[] {
  const out: DeckableCard[] = [];
  for (const c of cards) {
    if (c.type === 'meeting' && c.meeting && c.meeting.parsed) {
      const m = c.meeting as { recorded_at: string; duration_sec: number;
                                 transcript: string; parsed: any };
      const summary = m.parsed.summary || '';
      const date = (() => {
        try {
          return new Date(m.recorded_at).toLocaleString(undefined, {
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
          });
        } catch { return m.recorded_at; }
      })();
      const dur = (() => {
        const s = m.duration_sec || 0;
        const mn = Math.floor(s / 60), ss = s % 60;
        return mn > 0 ? `${mn}m ${ss}s` : `${ss}s`;
      })();
      const title = summary || `Meeting · ${dur}`;
      out.push({
        cardId: c.id,
        source: {
          kind: 'meeting',
          meeting: m as MeetingData,
          title,
        },
        display: {
          kindLabel: 'Meeting',
          title,
          subtitle:  `${dur} · ${date}`,
          summary,
        },
      });
    } else if (c.type === 'voice' && c.voice && c.voice.extracted) {
      const v = c.voice as { recorded_at: string; transcript: string;
                             extracted: any; entities?: any };
      const ex = v.extracted;
      const title = ex.property_name
                 || ex.address
                 || ex.summary
                 || 'Voice memo';
      const date = (() => {
        try {
          return new Date(v.recorded_at).toLocaleString(undefined, {
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
          });
        } catch { return v.recorded_at; }
      })();
      const subtitle = [
        ex.property_type,
        ex.location,
        date,
      ].filter(Boolean).join(' · ');
      out.push({
        cardId: c.id,
        source: {
          kind: 'memo',
          memo: {
            recorded_at: v.recorded_at,
            transcript:  v.transcript,
            extracted:   v.extracted,
            entities:    v.entities,
          } as MemoData,
          title,
        },
        display: {
          kindLabel: 'Voice memo',
          title,
          subtitle,
          summary: ex.summary || '',
        },
      });
    }
  }
  return out;
}
